import { message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  clearRecordCreationContext,
  openRecordModal,
} from '@app/components/RecordModal/model/recordModalSlice';
import { selectRecordCreationContext } from '@app/components/RecordModal/model/selectors';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { isValidTime } from '@app/shared/components/TimeInput';
import { useAppDispatch, useAppSelector } from '@app/store';
import type { RecordInterface, UpdatedEmployeeInterface } from '@app/types';

import {
  getCalendarGridStepHeight,
  resolveNewRecordEndTime,
  snapTimeToStep,
} from '@pages/Calendar/utils';

import { useCalendarPermissions } from '@pages/Calendar/hooks/useCalendarPermissions';

import { useCalendarContext } from '@pages/Calendar/context';
import type { CalendarBreak, CalendarEvent } from '@pages/Calendar/data';
import {
  useCalendarData,
  useCalendarRecordActions,
  useCalendarSlotMenu,
  useCalendarWebSocket,
} from '@pages/Calendar/hooks';

interface SelectedBreak {
  // Перерыв, который правим, или null — когда перерыв создаётся из меню шага
  // сетки (task/11149).
  id: number | null;
  employeeId: number;
  employeeName: string;
  date: string;
  timeS: string;
  timeE: string;
}

export const useCalendarLayout = () => {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();
  const {
    selectedDateMoment,
    intervalHeightPx,
    previewFields,
    gridStepMinutes,
    isTodaySelected,
    calendarDataRefreshKey,
    refreshCalendarData,
    updateDisplayedEmployeeIds,
    updateActiveWorkdayEmployeeIds,
  } = useCalendarContext();
  const { canCreateRecord, canShowContacts } = useCalendarPermissions();
  const recordCreationContext = useAppSelector(selectRecordCreationContext);
  const dateString = selectedDateMoment.format('YYYY-MM-DD');

  // Discard a deferred creation context (e.g. opened from the Lead Card) if the
  // user leaves the journal without picking a slot, so it never leaks into an
  // unrelated record created later.
  useEffect(
    () => () => {
      dispatch(clearRecordCreationContext());
    },
    [dispatch]
  );

  const {
    employees,
    events,
    isLoading,
    loadedDate,
    branchStartTime,
    branchEndTime,
    timeFormat,
    loadTeamJournal,
    updateEventsFromSocket,
  } = useCalendarData();

  const { checkRecordMove, applyRecordMove } = useCalendarRecordActions();
  const { slotMenu, openSlotMenuHandler, closeSlotMenuHandler } =
    useCalendarSlotMenu();
  const [selectedBreak, setSelectedBreak] = useState<SelectedBreak | null>(
    null
  );

  const visibleEmployeeIds = useMemo(
    () => employees.map(({ id }) => id),
    [employees]
  );

  const activeWorkdayEmployeeIds = useMemo(
    () =>
      employees
        .filter(({ hasActiveWorkday }) => hasActiveWorkday === true)
        .map(({ id }) => id),
    [employees]
  );

  useEffect(() => {
    loadTeamJournal(dateString);
  }, [dateString, loadTeamJournal, calendarDataRefreshKey]);

  useEffect(() => {
    if (loadedDate !== dateString) {
      updateDisplayedEmployeeIds([]);
      updateActiveWorkdayEmployeeIds([]);
      return;
    }

    updateDisplayedEmployeeIds(visibleEmployeeIds);
    updateActiveWorkdayEmployeeIds(activeWorkdayEmployeeIds);
  }, [
    activeWorkdayEmployeeIds,
    dateString,
    loadedDate,
    updateActiveWorkdayEmployeeIds,
    updateDisplayedEmployeeIds,
    visibleEmployeeIds,
  ]);

  useCalendarWebSocket({
    dateString,
    onData: updateEventsFromSocket,
  });

  const rawStartTime = useMemo(() => {
    const employeeStartTime = employees.reduce(
      (min, e) => (e.workStart < min ? e.workStart : min),
      branchStartTime
    );

    return events.reduce(
      (min, event) => (event.start < min ? event.start : min),
      employeeStartTime
    );
  }, [employees, events, branchStartTime]);

  const rawEndTime = useMemo(() => {
    const employeeEndTime = employees.reduce(
      (max, e) => (e.workEnd > max ? e.workEnd : max),
      branchEndTime
    );

    return events.reduce(
      (max, event) => (event.end > max ? event.end : max),
      employeeEndTime
    );
  }, [employees, events, branchEndTime]);

  const bookingStepMinutes = gridStepMinutes;

  const startTime = useMemo(
    () => snapTimeToStep(rawStartTime, bookingStepMinutes, 'floor'),
    [rawStartTime, bookingStepMinutes]
  );

  const endTime = useMemo(
    () => snapTimeToStep(rawEndTime, bookingStepMinutes, 'ceil'),
    [rawEndTime, bookingStepMinutes]
  );
  const gridStepHeightPx = useMemo(
    () =>
      getCalendarGridStepHeight({
        intervalHeightPx,
        stepMinutes: bookingStepMinutes,
      }),
    [bookingStepMinutes, intervalHeightPx]
  );

  const hasEmployees = employees.length > 0;

  const slotClickHandler = useCallback(
    (employeeId: string, time: string, freeEndTime: string) => {
      if (!canCreateRecord) {
        showNotification('У вас нет прав на создание записи', 'frontWarning');
        return;
      }

      const employee = employees.find(({ id }) => id === employeeId);

      if (!employee) {
        return;
      }

      if (employee.hasActiveWorkday !== true) {
        showNotification(
          'У исполнителя нет рабочего графика на этот день. Сначала добавьте рабочий график.',
          'frontWarning'
        );
        return;
      }

      const date = selectedDateMoment.format('YYYY-MM-DD');
      const [firstName, ...lastParts] = employee.name.split(' ');
      const lastName = lastParts.join(' ');

      dispatch(
        openRecordModal({
          mode: 'create',
          employee: {
            UserId: Number(employeeId),
            NameFirst: firstName,
            NameLast: lastName,
          } as unknown as UpdatedEmployeeInterface,
          time,
          endTime: resolveNewRecordEndTime({
            startTime: time,
            freeEndTime,
            gridStepMinutes: bookingStepMinutes,
          }),
          date,
          // Prefill the client when the journal was opened to create an
          // appointment for a specific lead/client (e.g. from the Lead Card).
          clientId: recordCreationContext?.clientId,
          clientName: recordCreationContext?.clientName ?? null,
        })
      );

      // The deferred creation context is single-use: consume it once the slot
      // (specialist + time) is chosen so it does not affect later records.
      if (recordCreationContext) {
        dispatch(clearRecordCreationContext());
      }
    },
    [
      bookingStepMinutes,
      canCreateRecord,
      employees,
      selectedDateMoment,
      dispatch,
      recordCreationContext,
      showNotification,
    ]
  );

  const onRecordClick = useCallback(
    (event: CalendarEvent) => {
      const employee = employees.find(({ id }) => id === event.employeeId);

      if (!employee) {
        return;
      }

      const date = selectedDateMoment.format('YYYY-MM-DD');
      const [firstName, ...lastParts] = employee.name.split(' ');
      const lastName = lastParts.join(' ');

      dispatch(
        openRecordModal({
          mode: 'edit',
          record: event.rawRecord as unknown as RecordInterface,
          employee: {
            UserId: Number(event.employeeId),
            NameFirst: firstName,
            NameLast: lastName,
          } as unknown as UpdatedEmployeeInterface,
          clientId: event.rawRecord.ClientId || undefined,
          clientName:
            [event.rawRecord.NameLast, event.rawRecord.NameFirst]
              .filter(Boolean)
              .join(' ') || null,
          date,
        })
      );
    },
    [employees, selectedDateMoment, dispatch]
  );

  const breakClickHandler = useCallback(
    (employeeId: string, calendarBreak: CalendarBreak) => {
      const employee = employees.find(({ id }) => id === employeeId);
      const { timeStart: timeS, timeEnd: timeE } = calendarBreak;

      if (
        employee?.hasActiveWorkday !== true ||
        !isValidTime(timeS) ||
        !isValidTime(timeE)
      ) {
        return;
      }

      setSelectedBreak({
        id: calendarBreak.id,
        employeeId: Number(employeeId),
        employeeName: employee.name,
        date: dateString,
        timeS,
        timeE,
      });
    },
    [dateString, employees]
  );

  // Пункт «Добавить перерыв» меню шага сетки открывает то же окно перерыва,
  // что и меню в шапке колонки исполнителя, но время берёт у самого шага.
  const slotBreakClickHandler = useCallback(
    (employeeId: string, time: string, endTime: string) => {
      const employee = employees.find(({ id }) => id === employeeId);

      if (
        employee?.hasActiveWorkday !== true ||
        !isValidTime(time) ||
        !isValidTime(endTime)
      ) {
        return;
      }

      setSelectedBreak({
        id: null,
        employeeId: Number(employeeId),
        employeeName: employee.name,
        date: dateString,
        timeS: time,
        timeE: endTime,
      });
    },
    [dateString, employees]
  );

  const closeBreakModalHandler = useCallback(() => {
    setSelectedBreak(null);
  }, []);

  const breakModalSuccessHandler = useCallback(() => {
    refreshCalendarData();
  }, [refreshCalendarData]);

  const onEventUpdate = useCallback(
    async (
      eventId: string,
      newStart: string,
      newEnd: string,
      newEmployeeId?: string
    ) => {
      const event = events.find(({ id }) => id === eventId);

      if (!event) {
        return;
      }

      const targetEmployeeId = newEmployeeId ?? event.employeeId;

      const moveParams = {
        recordId: Number(eventId),
        newStart,
        newEnd,
        newEmployeeId: Number(targetEmployeeId),
        date: dateString,
      };

      const checkResult = await checkRecordMove(moveParams);

      if (!checkResult.canMove) {
        message.warning(checkResult.message ?? 'Перенос записи невозможен');

        return;
      }

      await applyRecordMove(moveParams);
    },
    [events, checkRecordMove, applyRecordMove, dateString]
  );

  return {
    employees,
    events,
    isLoading,
    intervalHeightPx,
    gridStepHeightPx,
    startTime,
    endTime,
    boundaryEndTime: rawEndTime,
    bookingStepMinutes,
    timeFormat,
    hasEmployees,
    isTodaySelected,
    selectedBreak,
    slotClickHandler,
    slotBreakClickHandler,
    breakClickHandler,
    closeBreakModalHandler,
    breakModalSuccessHandler,
    onEventUpdate,
    onRecordClick,
    canShowContacts,
    previewFields,
    slotMenu,
    openSlotMenuHandler,
    closeSlotMenuHandler,
  };
};
